/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Observable } from 'rxjs/Observable';

import { BsModalService } from 'ngx-bootstrap/modal';

import { CacheService } from '../../global/ng2-cache/services/cache.service';
import { AdminService } from '../../services/admin.service';
import { SkuService } from '../../services/sku.service';
import { SessionService } from '../../services/session2.service';
import { ErrorService } from '../../services/error.service';
import { GlobalMessageService } from '../../services/global-message.service';
import { Constants } from '../../global/constants';
import { ConfirmDialogComponent } from '../../global/confirm-dialog/confirm-dialog.component';

import { EmailDetailService } from '../../services/email-detail.service';
import { ApproveQService } from '../../services/approve-q.service';
import { AggregateContactsService } from '../../services/aggregate-contacts.service';
import { EmailCampaignService } from '../../services/email-campaign.service';


@Component({
  selector: 'app-admin-account',
  templateUrl: './admin-account.component.html'
})
export class AdminAccountComponent implements OnInit {

  loadingPage: boolean = true;
  id: string = '';
  account: any = {};
  skus: any[] = [];
  activeSkus: any[] = [];
  stripePlans: any[] = [];
  activeStripePlans: any[] = [];
  coupons: any[] = [];

  activePlan: any;
  activeCoupon: any;
  totalCharge: number = 0;

  billingSaved: boolean = true;
  readyToCharge: boolean = false;
  notReadyReason: string = '';

  deleted: boolean = false;

  paymentFrequency: any[] = [
    { name: 'Annually' },
    { name: 'Monthly' }
  ];

  paymentMethods: any[] = [
    'Invoice'
  ];

  constructor(private route: ActivatedRoute,
    private cacheService: CacheService,
    private adminService: AdminService,
    private skuService: SkuService,
    private modalService: BsModalService,
    private aggregateContactsService: AggregateContactsService,
    private sessionService: SessionService,
    private errorService: ErrorService,
    private constants: Constants,
    private globalMessageService: GlobalMessageService,
    private emailDetailService: EmailDetailService,
    private approveQService: ApproveQService,
    private emailCampaignService: EmailCampaignService) { }

  ngOnInit() {
    this.route.params.subscribe(
      params => {
        this.id = params['id'];
        if (!this.cacheService.get('session')) {
          this.sessionService.getSession(true).subscribe(
            (session: any) => {
              this.initView();
            }
          )
        }
        else {
          this.initView();
        }
      }
    )
  }

  changeSku() {
    this.skus.forEach((sku) => {
      if (sku._id == this.account.subscription.sku_id) {
        this.account.subscription.reply_limit = sku.reply_limit;
      }
    });
  }

  changeStatus() {
    this.account.subscription.status_date = new Date();
  }

  saveEdition() {
    var update = {
      'name': this.account.name,
      'obfuscate_days': parseInt(this.account.obfuscate_days ? this.account.obfuscate_days : '0'),
      'subscription.sku_id': this.account.subscription.sku_id,
      'subscription.reply_limit': this.account.subscription.reply_limit,
      'subscription.status': this.account.subscription.status,
      'subscription.status_date': this.account.subscription.status_date,
      'subscription.delete_status': 0,
      'subscription.tier': this.account.subscription.tier,
      'billing.payment_method': this.account.billing.payment_method,
      'hubspot_id': this.account.hubspot_id && this.account.hubspot_id.length > 0 ? this.account.hubspot_id : null
    };

    if (this.account.billing.payment_method == 'Invoice') {
      update['billing.payment_frequency'] = this.account.billing.payment_frequency;
      update['billing.stripe_plan_id'] = null;
      update['billing.coupon'] = { id: null };
      var price = +this.account.billing.price;
      if (this.account.billing.payment_frequency == 'Monthly') {
        price *= 0.8;
      }
      else {
        price /= 12;
      }
      update['billing.price'] = price;
    }
    this.adminService.saveAccount(this.id, update).subscribe(
      (result: any) => {
        this.globalMessageService.message('Account saved');
      },
      error => {
        this.errorService.log(error, 'admin-account.component', 'saveEdition');
        this.globalMessageService.error();
      }
    )
  }

  cancelName() {
    this.initView();
  }

  changePlan() {
    this.stripePlans.forEach((sp) => {
      if (sp._id == this.account.billing.stripe_plan_id) {
        this.activePlan = sp;
        this.account.billing.price = sp.amount / 100;
      }
    });

    this.billingSaved = false;
    this.calcTotal();
  }

  changeDiscount() {
    this.coupons.forEach((c) => {
      if (c.id == this.account.billing.coupon.id) {
        this.activeCoupon = c;
        this.account.billing.coupon = {
          id: c.id,
          percent_off: c.percent_off,
          amount_off: c.amount_off
        }
      }
    });
    this.billingSaved = false;
    this.calcTotal();
  }

  changeTax() {
    this.billingSaved = false;
    this.calcTotal();
  }

  toggleFeature(feature: string) {
    this.account.features[feature] = !this.account.features[feature];
    const update = {
      features: this.account.features
    };

    this.adminService.saveAccount(this.id, update).subscribe(
      (result: any) => {
        this.globalMessageService.message('Account saved');
      },
      (error) => {
        this.errorService.log(error, 'admin-account.component', 'toggleFeature');
        this.globalMessageService.error();
      }
    );
  }

  toggleQueueFeature(feature: string) {

    const currentState = this.account.features[feature];

    this.account.features[feature] = !currentState;
    const update = {
      features: this.account.features
    };

    const actions = [];
    actions.push(this.adminService.saveAccount(this.id, update));
    if (currentState === true) {
      actions.push(this.approveQService.toggleReviewQueueOff(this.id));
      actions.push(this.emailDetailService.toggleReviewQueueOff(this.id));
    }

    Observable.forkJoin(...actions)
      .subscribe(
        (result: any) => {
          this.globalMessageService.message('Account saved');
        },
      (error) => {
        this.globalMessageService.error();
        throw error;
      });
  }

  changeSpamThreshold() {
    this.account.features.spam_threshold = parseFloat(this.account.features.spam_threshold);
    if (this.account.features.spam_threshold > .25 && this.account.features.spam_threshold <= 1) {
      let update: any = {
        features: this.account.features
      }

      this.adminService.saveAccount(this.id, update).subscribe(
        (result: any) => {
          this.globalMessageService.message('Account saved');
        },
        error => {
          this.errorService.log(error, 'admin-account.component', 'toggleFeature');
          this.globalMessageService.error();
        }
      );
    }
  }

  changeAdminQCutoffHours() {
    this.account.features.adminQueueCutoffHours = parseInt(this.account.features.adminQueueCutoffHours, 10);
    if (this.account.features.adminQueueCutoffHours > 0) {
      const update: any = {
        features: this.account.features
      };

      this.adminService.saveAccount(this.id, update).subscribe(
        () => {
          this.globalMessageService.message('Account saved');
        },
        (error) => {
          this.errorService.log(error, 'admin-account.component', 'toggleFeature');
          this.globalMessageService.error();
        }
      );
    }
  }

  saveBilling() {
    var price = this.activePlan.amount / 100;
    var frequency = 'Monthly';
    if (this.activePlan.interval == 'year') {
      price = price / 12;
      frequency = 'Annually';
    }
    else {
      price *= 0.8;
    }

    var update = {
      'billing.price': price,
      'billing.payment_frequency': frequency,
      'billing.stripe_plan_id': this.account.billing.stripe_plan_id,
      'billing.coupon': this.account.billing.coupon,
      'billing.tax_percent': +this.account.billing.tax_percent
    };

    this.adminService.saveAccount(this.id, update).subscribe(
      (result: any) => {
        this.globalMessageService.message('Account saved');
      },
      error => {
        this.errorService.log(error, 'admin-account.component', 'saveBilling');
        this.globalMessageService.error();
      }
    )
    this.billingSaved = true;
    this.checkReady();
  }

  chargeCustomer() {
    let me = this;
    this.modalService.show(ConfirmDialogComponent, Object.assign({}, {
      initialState: {
        message: `Please confirm that you'd like to submit payment detail to Stripe and start charging this customer. Both monthly and annual subscriptions will be billed immedately and set up with automatic renewal.`,
        confirm: function () {
          me.adminService.startSubscription(me.id).subscribe(
            (result: any) => {
              me.globalMessageService.message('Payment information submitted successfully');
            },
            error => {
              console.error(error);
              me.errorService.log(error, 'admin-account.component', 'chargeCustomer');
              me.globalMessageService.error(JSON.stringify(error));
            }
          )
        }
      }
    }));
  }

  private initView() {
    this.loadingPage = true;

    // need to reset member variables so Cancel works correctly
    this.activePlan = null;
    this.activeCoupon = null;
    this.totalCharge = 0;
    this.billingSaved = true;
    this.readyToCharge = false;
    this.notReadyReason = '';

    Observable.forkJoin(
      this.adminService.getAccount(this.id),
      this.skuService.getSkus()
    ).subscribe(
      (result: any) => {
        this.account = result[0];
        this.skus = result[1];
        this.activeSkus = [];

        this.skus.forEach((sku) => {
          if (sku.active) {
            this.activeSkus.push(sku);
          }
        });

        this.activeSkus.sort((a, b) => {
          if (a.name < b.name) {
            return -1;
          }
          else {
            return 1;
          }
        });

        this.coupons.sort((a, b) => {
          if (a.percent_off && b.amount_off) {
            return -1;
          }
          else if (a.percent_off && b.percent_off) {
            if (a.percent_off < b.percent_off) {
              return -1;
            }
            else {
              return 1;
            }
          }
          else if (a.amount_off && b.amount_off) {
            if (a.amount_off < b.amount_off) {
              return -1;
            }
            else {
              return 1;
            }
          }
          else {
            return 1;
          }
        });

        this.stripePlans.forEach((plan) => {
          if (plan.metadata.active) {
            this.activeStripePlans.push(plan);
          }
        });

        this.activeStripePlans.sort((a, b) => {
          if (a.interval < b.interval) {
            return -1
          }
          else if (a.interval > b.interval) {
            return 1;
          }
          else {
            if (a.amount < b.amount) {
              return -1;
            }
            else {
              return 1;
            }
          }
        });

        if (this.account.billing.stripe_plan_id) {
          this.stripePlans.forEach((sp) => {
            if (sp._id == this.account.billing.stripe_plan_id) {
              this.activePlan = sp;
            }
          });
        }

        if (this.account.billing.coupon) {
          this.coupons.forEach((c) => {
            if (c.id == this.account.billing.coupon.id) {
              this.activeCoupon = c;
            }
          });
        }

        if (this.account.billing.payment_method == 'Invoice' && this.account.billing.payment_frequency == 'Annually') {
          this.account.billing.price *= 12;
        }

        if (!this.account.subscription.status) {
          this.account.subscription.status = 'active';
          this.account.subscription.status_date = new Date()
        }

        this.calcTotal();
        this.loadingPage = false;
      },
      error => {
        this.errorService.log(error, 'admin-account.component', 'initView');
        this.globalMessageService.error();
      }
    )
  }

  private calcTotal() {
    this.totalCharge = 0;
    if (this.activePlan) {
      this.totalCharge = this.activePlan.amount / 100;
    }
    if (this.activeCoupon) {
      if (this.activeCoupon.percent_off) {
        this.totalCharge = this.totalCharge - (this.totalCharge * (this.activeCoupon.percent_off / 100));
      }
      else {
        this.totalCharge = this.totalCharge - (this.activeCoupon.amount_off / 100);
      }
    }

    if (this.account.billing.tax_percent > 0) {
      this.totalCharge = this.totalCharge + (this.totalCharge * this.account.billing.tax_percent / 100);
    }
    this.checkReady();
  }

  checkReady() {
    this.readyToCharge = true;
    this.notReadyReason = '';
    if (!this.account.billing.credit_card) {
      this.readyToCharge = false;
      this.notReadyReason = 'Customer has not added a credit card';
    }
    else if (this.account.billing.credit_card && this.account.billing.credit_card.used) {
      this.readyToCharge = false;
      this.notReadyReason = 'Credit card has been used';
    }

    if (!this.account.billing.stripe_plan_id) {
      this.readyToCharge = false;
      this.notReadyReason = 'No plan selected';
    }
    else if (!this.billingSaved) {
      this.readyToCharge = false;
      this.notReadyReason = 'Billing information has not been saved';
    }
  }

  performBackfill() {
    this.aggregateContactsService.backfill30Days(this.id).subscribe(
      (result: any) => {
        this.globalMessageService.message("Dashboard Backfill Completed");
    });
  }

  createTemplateCampaigns() {
    this.emailCampaignService.createCampaignTemplates(this.id).subscribe(
      (result: any) => {
        this.globalMessageService.message("Email Campaigns Created");
    });
  }
}



// WEBPACK FOOTER //
// ./client/app/admin/admin-account/admin-account.component.ts